import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Day-specific midday nudges
const getDayContent = () => {
  const dayOfWeek = new Date().getDay();
  const dayContent = {
    0: { // Sunday
      theme: "Rest & Recharge",
      nudge: "Sunday afternoons can feel directionless. That's okay. You don't need to be productive—just intentional.",
      reflection: "Have you given yourself permission to rest without guilt today?",
      actionPrompt: "One thing I can do right now that honors my values:",
      affirmation: "Rest is productive. Recovery is active."
    },
    1: { // Monday
      theme: "Momentum Check",
      nudge: "Monday mornings are about intention. Monday afternoons are about following through.",
      reflection: "Are you still aligned with your morning intention?",
      actionPrompt: "One small action I can take right now:",
      affirmation: "Small steps compound. Keep going."
    },
    2: { // Tuesday
      theme: "Energy Check",
      nudge: "Tuesday afternoons can bring that first dip in energy. Notice it. Don't fight it.",
      reflection: "What does my body and mind need right now?",
      actionPrompt: "One thing that would help me recharge:",
      affirmation: "I can pause without losing progress."
    },
    3: { // Wednesday
      theme: "Halfway Pause",
      nudge: "You're halfway through the week. How's it going? Really.",
      reflection: "Am I pushing too hard, or not enough?",
      actionPrompt: "One adjustment I can make for the rest of the week:",
      affirmation: "I can recalibrate without starting over."
    },
    4: { // Thursday
      theme: "Gratitude Pause",
      nudge: "Thursday afternoons: take a moment to notice what's working.",
      reflection: "What's gone better than expected this week?",
      actionPrompt: "One thing I'm proud of from this week:",
      affirmation: "Noticing progress creates more progress."
    },
    5: { // Friday
      theme: "Weekend Prep",
      nudge: "Friday afternoons bring freedom—and vulnerability. Plan ahead.",
      reflection: "What's my plan for managing triggers this weekend?",
      actionPrompt: "One thing I'll do this weekend that aligns with who I'm becoming:",
      affirmation: "Weekends are opportunities, not threats."
    },
    6: { // Saturday
      theme: "Freedom & Intention",
      nudge: "Saturday afternoons: unstructured time is a test. You're choosing how to use it.",
      reflection: "Am I using my free time in ways that serve me?",
      actionPrompt: "One intentional thing I'll do with the rest of my day:",
      affirmation: "I am capable of choosing well."
    }
  };
  return dayContent[dayOfWeek];
};

const urgencyCheckOptions = [
  {
    value: 'good',
    label: "I'm doing well",
    icon: '✨',
    color: '#06D6A0'
  },
  {
    value: 'urge',
    label: "I'm feeling an urge",
    icon: '🌊',
    color: '#00B4D8'
  },
  {
    value: 'struggling',
    label: "I'm struggling",
    icon: '⚠️',
    color: '#FFB627'
  }
];

export default function MiddayNudge() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const dayContent = getDayContent();
  const [formData, setFormData] = useState({
    status_check: '',
    reflection: '',
    action_commitment: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.status_check) {
      toast.error('Please share how you\'re doing');
      return;
    }
    
    if (!formData.action_commitment.trim()) {
      toast.error('Please commit to one action');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/check-ins`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          type: 'midday',
          ...formData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save nudge');
      }

      toast.success('Great. Keep going.');
      navigate('/dashboard', { state: { checkInComplete: true } });
    } catch (error) {
      toast.error(error.message || 'Failed to save nudge');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen landing-hero flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 bg-[#3A4550] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-[#6F8C9B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F8F9FA]" data-testid="midday-nudge-title">
            Midday Check
          </h1>
          <p className="text-base text-[#ADB5BD] leading-relaxed">
            {dayContent.theme}
          </p>
        </div>

        {/* Daily Nudge Card */}
        <div className="bg-gradient-to-br from-[#D4A574]/20 to-[#8FAF9A]/20 rounded-2xl p-5 border border-[#D4A574]/30">
          <p className="text-center text-[#F8F9FA] font-medium">
            {dayContent.nudge}
          </p>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit} className="bg-[#2D3640] rounded-2xl border border-[#4A5568] shadow-sm p-6 space-y-6">
          {/* Status Check */}
          <div className="space-y-3">
            <Label className="text-base font-medium text-[#F8F9FA]">
              How are you doing right now?
            </Label>
            <RadioGroup
              value={formData.status_check}
              onValueChange={(value) => {
                setFormData({ ...formData, status_check: value });
                // Navigate immediately if struggling or urge
                if (value === 'struggling' || value === 'urge') {
                  setTimeout(() => {
                    navigate('/sos', { state: { from: 'midday', status: value } });
                  }, 500);
                }
              }}
              className="space-y-3"
            >
              {urgencyCheckOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md"
                  style={{ 
                    borderColor: formData.status_check === option.value ? option.color : '#4A5568',
                    backgroundColor: formData.status_check === option.value ? option.color + '10' : '#2D3640'
                  }}
                  data-testid={`status-option-${option.value}`}
                >
                  <RadioGroupItem value={option.value} id={option.value} className="mt-0.5" />
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-2xl">{option.icon}</span>
                    <span className="text-base text-[#F8F9FA] leading-relaxed font-medium">
                      {option.label}
                    </span>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* Show rest of form only if doing well */}
          {formData.status_check === 'good' && (
            <>
              {/* Reflection */}
              <div className="space-y-2">
                <Label className="text-base font-medium text-[#F8F9FA]">
                  {dayContent.reflection}
                </Label>
                <Textarea
                  value={formData.reflection}
                  onChange={(e) => setFormData({ ...formData, reflection: e.target.value })}
                  placeholder="Quick reflection..."
                  className="min-h-24 rounded-xl border-[#4A5568] focus:border-[#6F8C9B] focus:ring-[#6F8C9B]/20 text-base"
                  data-testid="reflection-input"
                />
              </div>

              {/* Action Commitment */}
              <div className="space-y-2">
                <Label className="text-base font-medium text-[#F8F9FA]">
                  {dayContent.actionPrompt}
                </Label>
                <Textarea
                  value={formData.action_commitment}
                  onChange={(e) => setFormData({ ...formData, action_commitment: e.target.value })}
                  placeholder="I will..."
                  className="min-h-20 rounded-xl border-[#4A5568] focus:border-[#6F8C9B] focus:ring-[#6F8C9B]/20 text-base"
                  data-testid="action-input"
                />
              </div>

              {/* Affirmation */}
              <div className="p-4 bg-gradient-to-br from-[#8FAF9A]/10 to-[#6F8C9B]/10 rounded-xl border border-[#8FAF9A]/30">
                <p className="text-center text-[#F8F9FA] font-medium italic">
                  &quot;{dayContent.affirmation}&quot;
                </p>
              </div>

              {/* Submit Button */}
              <div className="space-y-3 pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="button-primary w-full rounded-full px-8 py-6 h-auto text-lg font-medium"
                  data-testid="submit-nudge-btn"
                >
                  {loading ? 'Saving...' : 'Continue'}
                </Button>
                
                <Button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  variant="ghost"
                  className="w-full rounded-full px-6 py-4 h-auto text-base text-[#ADB5BD]"
                  data-testid="cancel-nudge-btn"
                >
                  Skip for now
                </Button>
              </div>
            </>
          )}

          {/* Message for urge/struggling */}
          {(formData.status_check === 'urge' || formData.status_check === 'struggling') && (
            <div className="text-center space-y-4 py-6">
              <p className="text-base text-[#F8F9FA] font-medium">
                Taking you to support tools...
              </p>
              <div className="animate-spin w-8 h-8 border-4 border-[#6F8C9B] border-t-transparent rounded-full mx-auto"></div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
